import { useMemo } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { FetchEngageMessagesParams, fetchEngageMessages, fetchTrackableLetters } from 'api/engage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteScroll } from 'customHooks/useInfiniteScroll';
import { filterUndefined } from 'helpers/arrays';
import { transformInfiniteQueryOptions } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { EngageMessage } from 'types/domain';
import {
    GivebutterInfiniteQueryParams,
    GivebutterUseSuspenseInfiniteQueryOptions,
} from 'types/react-query';

const DEFAULT_PAGE_LIMIT = 20;

type UseInfiniteMessagesFetchStrategy = 'trackableMailings' | 'mailingsRecipients' | 'all';

interface UseInfiniteMessagesOptions
    extends GivebutterUseSuspenseInfiniteQueryOptions<EngageMessage> {
    strategy?: UseInfiniteMessagesFetchStrategy;
}

const useInfiniteMessages = (
    params: GivebutterInfiniteQueryParams<FetchEngageMessagesParams> = {},
    options: UseInfiniteMessagesOptions = {},
) => {
    const { per_page = DEFAULT_PAGE_LIMIT, ...rest } = params;
    const { strategy = 'all', ...restOptions } = options;
    const { accountId } = useCurrentAccount();
    const isMailingFilter = strategy === 'trackableMailings' || strategy === 'mailingsRecipients';

    const channel: FetchEngageMessagesParams['channel'] = useMemo(() => {
        return isMailingFilter ? ['letter'] : undefined;
    }, [isMailingFilter]);

    const status: FetchEngageMessagesParams['status'] = useMemo(() => {
        return isMailingFilter ? ['sent'] : undefined;
    }, [isMailingFilter]);

    const nextParams = { per_page, channel, status, ...rest };

    const fetchMethod = useMemo(() => {
        if (strategy === 'trackableMailings') {
            return fetchTrackableLetters;
        }
        return fetchEngageMessages;
    }, [strategy]);

    const infiniteQueryData = useSuspenseInfiniteQuery({
        initialPageParam: 1,
        queryKey: filterUndefined([
            ...accountQueryKeys.engageMessagesWithFilters(accountId, nextParams),
            strategy,
        ]),
        queryFn: ({ pageParam }) => fetchMethod(accountId, { page: pageParam, ...nextParams }),
        ...transformInfiniteQueryOptions<EngageMessage>(),
        ...restOptions,
    });
    const ref = useInfiniteScroll(infiniteQueryData);

    return { ...infiniteQueryData, ref };
};

export { useInfiniteMessages };
export type { UseInfiniteMessagesFetchStrategy };
